import { useState, lazy, Suspense, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { openBigFollowChat, setOpenChatPending } from './chatController';
import { useTranslations } from '@/hooks/useTranslations';

// Lade das schwere ChatWidget (framer-motion + react-markdown ~120 KB)
// erst wenn der Nutzer es öffnet. Spart 120 KB im Critical Path.
const ChatWidget = lazy(() => import('./ChatWidget'));

const STORAGE_KEY = 'bigfollow_support';

declare global {
  interface Window {
    openBigFollowChat?: () => void;
  }
}


export default function ChatWidgetLauncher() {
  const { t } = useTranslations();
  const [shouldLoad, setShouldLoad] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [isOnline, setIsOnline] = useState(false);

  // Globale Funktion, damit andere Seiten (z. B. Kontakt) den Chat zuverlässig öffnen können.
  useEffect(() => {
    const handler = () => {
      const wasOpen = openBigFollowChat();
      if (!wasOpen) {
        // Widget ist noch nicht geladen: jetzt nachladen und Öffnen merken.
        setOpenChatPending(true);
        setShouldLoad(true);
      }
    };
    window.openBigFollowChat = handler;
    return () => {
      if (window.openBigFollowChat === handler) {
        window.openBigFollowChat = undefined;
      }
    };
  }, []);

  // Hat der Nutzer bereits eine Session? Dann Widget vorab laden,
  // damit eingehende Nachrichten/Notifications funktionieren.
  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) {
        setHasSession(true);
        // Mit kleinem Delay nach idle
        const t = setTimeout(() => setShouldLoad(true), 2000);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);

  // Nur Online-Status laden für den Indicator-Punkt
  useEffect(() => {
    if (shouldLoad) return;
    // Status-Abfrage stark verzögern (8s nach Load), damit sie GARANTIERT
    // weder LCP noch den initialen kritischen Pfad blockiert. Der grüne
    // Online-Indikator ist rein dekorativ und darf später erscheinen.
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      supabase.from('chat_settings').select('is_online').eq('id', 1).single().then(({ data }) => {
        if (!cancelled && data) setIsOnline(data.is_online);
      });
    };
    const startAfterLoad = () => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      // 8 Sekunden nach Load + idle slot
      const delayedRun = () => {
        if (cancelled) return;
        if (typeof w.requestIdleCallback === 'function') {
          const id = w.requestIdleCallback(run, { timeout: 5000 });
          // store for cleanup via closure-cancelled flag
          return () => w.cancelIdleCallback?.(id);
        }
        run();
      };
      const t = setTimeout(delayedRun, 8000);
      return () => { cancelled = true; clearTimeout(t); };
    };
    if (document.readyState === 'complete') return startAfterLoad();
    const onLoad = () => { startAfterLoad(); };
    window.addEventListener('load', onLoad, { once: true });
    return () => { cancelled = true; window.removeEventListener('load', onLoad); };
  }, [shouldLoad]);

  if (shouldLoad) {
    return (
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    );
  }

  return (
    <button
      onClick={() => setShouldLoad(true)}
      aria-label={t('chat.open_aria')}
      className="fixed bottom-4 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105"
    >
      <MessageCircle className="h-6 w-6" />
      {isOnline && (
        <span className="absolute right-1 top-1 h-3 w-3 rounded-full bg-green-500 ring-2 ring-background" />
      )}
      {hasSession && (
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-background" />
      )}
    </button>
  );
}
